import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

const authConfig = {
  authority: import.meta.env.VITE_AUTH_URL || 'https://localhost:5001',
  client_id: import.meta.env.VITE_CLIENT_ID || 'afericoes',
  redirect_uri: `${window.location.origin}/callback`,
  response_type: 'code',
  scope: import.meta.env.VITE_SCOPE || 'openid profile email offline_access afericoes-api',
  post_logout_redirect_uri: window.location.origin,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  // PKCE é recomendado para clientes públicos (SPA)
  // Se o servidor exigir client_secret, você precisará configurar isso no backend
  // e usar um fluxo diferente (não recomendado para SPA)
  automaticSilentRenew: true,
  silent_redirect_uri: `${window.location.origin}/silent-callback.html`
};

console.log('[OIDC] Configuração:', {
  authority: authConfig.authority,
  client_id: authConfig.client_id,
  redirect_uri: authConfig.redirect_uri,
  scope: authConfig.scope
});

const userManager = new UserManager(authConfig);

// Armazenar access_token em memória (não persistir por segurança)
let accessToken = null;
let tokenExpiry = null;

// Carregar token do usuário atual
userManager.getUser().then((user) => {
  if (user) {
    console.log('[OIDC] Usuário encontrado no storage:', {
      sub: user.profile?.sub,
      expired: user.expired,
      expires_at: user.expires_at
    });
    accessToken = user.access_token;
    tokenExpiry = user.expires_at ? user.expires_at * 1000 : null;
  } else {
    console.log('[OIDC] Nenhum usuário encontrado no storage');
  }
});

export const signIn = async () => {
  console.log('[OIDC] Iniciando signIn - redirecionando para:', authConfig.authority);
  try {
    await userManager.signinRedirect();
  } catch (error) {
    console.error('[OIDC] Erro ao fazer signIn:', error);
    throw error;
  }
};

export const signOut = async () => {
  try {
    // Obter usuário atual para id_token_hint
    const user = await userManager.getUser();
    
    // Limpar dados locais primeiro
    accessToken = null;
    tokenExpiry = null;
    
    // Fazer logout no servidor de autenticação usando o endpoint /connect/logout
    // O signoutRedirect já usa o endpoint correto baseado no authority
    // Passar id_token_hint e post_logout_redirect_uri para Single Logout (SLO)
    await userManager.signoutRedirect({
      id_token_hint: user?.id_token,
      post_logout_redirect_uri: window.location.origin
    });
  } catch (error) {
    console.error('[OIDC] Erro ao fazer logout:', error);
    // Mesmo com erro, limpar dados locais e redirecionar
    accessToken = null;
    tokenExpiry = null;
    await userManager.removeUser();
    // Redirecionar para a página inicial
    window.location.href = window.location.origin;
    throw error;
  }
};

// Flag para evitar processar callback múltiplas vezes
let isProcessingCallback = false;

export const handleCallback = async () => {
  // Verificar se já está processando
  if (isProcessingCallback) {
    console.log('[OIDC] Callback já está sendo processado, aguardando...');
    // Aguardar um pouco e verificar se o usuário foi carregado
    await new Promise(resolve => setTimeout(resolve, 500));
    const user = await userManager.getUser();
    if (user && !user.expired) {
      return user;
    }
    throw new Error('Callback já está sendo processado');
  }

  // Verificar se há código na URL
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  if (!code) {
    throw new Error('Nenhum código de autorização encontrado na URL');
  }

  isProcessingCallback = true;
  console.log('[OIDC] Processando callback...');
  
  try {
    const user = await userManager.signinRedirectCallback();
    console.log('[OIDC] Callback processado com sucesso:', {
      sub: user.profile?.sub,
      access_token: user.access_token ? 'presente' : 'ausente',
      expires_at: user.expires_at
    });
    accessToken = user.access_token;
    tokenExpiry = user.expires_at ? user.expires_at * 1000 : null;
    return user;
  } catch (error) {
    console.error('[OIDC] Erro ao processar callback:', error);
    // Limpar flag em caso de erro para permitir nova tentativa
    isProcessingCallback = false;
    throw error;
  } finally {
    // Manter flag como true após sucesso para evitar reprocessamento
    // A flag será resetada quando a página for recarregada
  }
};

export const getAccessToken = async () => {
  // Não tentar fazer refresh se estiver na página de callback
  // para evitar loops de autenticação
  if (window.location.pathname === '/callback') {
    const user = await userManager.getUser();
    if (user && !user.expired) {
      accessToken = user.access_token;
      tokenExpiry = user.expires_at ? user.expires_at * 1000 : null;
      return accessToken;
    }
    return null;
  }

  // Verificar se token expirou
  if (tokenExpiry && Date.now() >= tokenExpiry) {
    try {
      await refreshToken();
    } catch (error) {
      console.warn('[OIDC] Erro ao fazer refresh em getAccessToken:', error);
      // Não propagar erro, apenas retornar null
      // O router vai detectar que não está autenticado
    }
  }

  if (accessToken) {
    return accessToken;
  }

  // Tentar obter do usuário atual
  const user = await userManager.getUser();
  if (user && !user.expired) {
    accessToken = user.access_token;
    tokenExpiry = user.expires_at ? user.expires_at * 1000 : null;
    return accessToken;
  }

  return null;
};

export const refreshToken = async () => {
  const user = await userManager.getUser();
  if (user) {
    try {
      const refreshedUser = await userManager.signinSilent();
      accessToken = refreshedUser.access_token;
      tokenExpiry = refreshedUser.expires_at ? refreshedUser.expires_at * 1000 : null;
      return refreshedUser;
    } catch (error) {
      console.error('[OIDC] Erro ao fazer refresh do token:', error);
      // Se refresh falhar, limpar usuário e não fazer signIn automaticamente
      // O router vai detectar que não está autenticado e redirecionar
      await userManager.removeUser();
      accessToken = null;
      tokenExpiry = null;
      throw error;
    }
  } else {
    // Se não há usuário, não fazer signIn automaticamente
    // Deixar o router tratar
    throw new Error('Nenhum usuário encontrado para refresh');
  }
};

export const isAuthenticated = async () => {
  try {
    console.log('[OIDC] Chamando userManager.getUser()...');
    const user = await Promise.race([
      userManager.getUser(),
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout ao obter usuário do UserManager')), 5000);
      })
    ]);
    
    const authenticated = user !== null && !user.expired;
    console.log('[OIDC] Verificando autenticação:', {
      hasUser: user !== null,
      expired: user?.expired,
      authenticated
    });
    return authenticated;
  } catch (error) {
    console.error('[OIDC] Erro ao verificar autenticação:', error);
    console.error('[OIDC] Stack:', error.stack);
    // Em caso de erro, assumir não autenticado
    return false;
  }
};

export const getUser = async () => {
  return await userManager.getUser();
};

export const clearUser = async () => {
  accessToken = null;
  tokenExpiry = null;
  await userManager.removeUser();
};

// Eventos do UserManager
userManager.events.addUserLoaded((user) => {
  accessToken = user.access_token;
  tokenExpiry = user.expires_at ? user.expires_at * 1000 : null;
});

userManager.events.addUserUnloaded(() => {
  accessToken = null;
  tokenExpiry = null;
});

